import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import DocumentsTable from 'modules/documents/components/DocumentsTable';
import { generateIrpfCertificate } from 'modules/partners/components/PartnerAside/utils/generateIrpfCertificate';
import { useValidateIrpfPartner } from 'modules/partners/hooks/useValidateIrpfPartner';
import { useValidatePartnerEncumbrances } from 'modules/partners/hooks/useValidatePartnerEncumbrances';
import { generateCapitalHoldingCertificate } from 'modules/partners/components/PartnerAside/utils/generateCapitalHoldingCertificate';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';

import { Document, Society } from 'types';

import { hasShowDetails, isPlatformAXA } from 'utils/showDetails';
import { AUTOGENERATED_CERT_DOC_IDS } from '../constants/autogeneratedCertificateDocIds';
import { usePartnerDocumentsView } from '../context/PartnerDocumentsViewContext';

import GenerateCertificateTableDownloadButton from './GenerateCertificateTableDownloadButton';

type DocumentsModuleProps = {
  isCertificatesValueSharesModule: boolean;
};

const encumbrancesReferenceDate = new Date(
  new Date().getFullYear() - 1,
  11,
  31
);

const hasSharesOwnershipTemplate = (
  actualSociety: Society,
  excludeFiscalYear2024Templates = false
): boolean => {
  const has2024Templates =
    !!actualSociety?.templates?.sharesOwnershipCertificateES_2024 ||
    !!actualSociety?.templates?.sharesOwnershipCertificateCA_2024;

  return (
    !!actualSociety?.templates?.sharesOwnershipCertificateES ||
    !!actualSociety?.templates?.sharesOwnershipCertificateCA ||
    (!excludeFiscalYear2024Templates && has2024Templates) ||
    !!actualSociety?.templates?.sharesOwnershipCertificateES_2025 ||
    !!actualSociety?.templates?.sharesOwnershipCertificateCA_2025 ||
    !!actualSociety?.templates?.sharesOwnershipCertificatePostFusionES ||
    !!actualSociety?.templates?.sharesOwnershipCertificatePostFusionCA
  );
};

const hasCapitalHoldingTemplate = (actualSociety: Society): boolean =>
  !!actualSociety?.templates?.capitalHoldingCertificateES ||
  !!actualSociety?.templates?.capitalHoldingCertificateCA;

const virtualAutogeneratedDocBase: Partial<Document> = {
  url: '',
  size: 0,
  base64: '',
  lastAccess: new Date(),
  isCommon: false,
  isGenerated: true,
  isFavorite: false,
  isForAdmins: false,
  editorModel: '',
  envelopeId: '',
  reportStatus: '',
  linkedTo: [],
  folder: '',
  author: '',
  fileType: fileTypes.PDF,
  category: documentTypes.CERTIFICATES,
  subcategory: 'AUTOGENERATED',
};

const DocumentsModule: FC<DocumentsModuleProps> = ({
  isCertificatesValueSharesModule,
}) => {
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  ) as Society;

  const { isAdmin, isReadOnly } = useGetPartnersSectionPermissions().data;
  const { data, actions } = usePartnerDocumentsView();

  const { partnerId, partner, sharesOwnershipDocuments } = data;

  // eslint-disable-next-line no-unused-vars
  const _hasEncumbrancesAtDate = useValidatePartnerEncumbrances({
    partnerId,
    date: encumbrancesReferenceDate,
  });

  const hasActualShares = Boolean((partner?.sharesCount?.actual || 0) > 0);
  const hasActualEncumbrances = Boolean(
    (partner?.sharesCount?.coOwned || 0) > 0
  );

  const hasSharesOwnershipCertificate = Boolean(
    hasSharesOwnershipTemplate(actualSociety, !isAdmin) && hasActualShares
  );
  if (hasShowDetails()) {
    console.log(
      '🚀 cclog ~ DocumentsModule ~ hasSharesOwnershipCertificate:',
      hasSharesOwnershipCertificate
    );
  }

  const hasCapitalHoldingCertificate = Boolean(
    hasCapitalHoldingTemplate(actualSociety) &&
      hasActualShares &&
      !hasActualEncumbrances &&
      isPlatformAXA
  );

  const hasIrpfCertificate = useValidateIrpfPartner({
    partnerId,
    partner: partner || undefined,
  });

  const title = isCertificatesValueSharesModule
    ? t('CertificatesValueShares')
    : t('Documents');

  const subtitle = isCertificatesValueSharesModule
    ? t('CertificatesValueSharesMessage')
    : t('PartnerDocumentationPar', {
        partnerName: data.partnerName,
        societyName: actualSociety?.name,
      });

  const documents = isCertificatesValueSharesModule
    ? data.certificateValueSharesDocuments
    : data.otherDocuments;

  const autogeneratedDocuments = useMemo(() => {
    const list: Document[] = [];
    const societyId = String(actualSociety?._id || '');
    const now = new Date();

    if (hasSharesOwnershipCertificate) {
      const ownershipCertsForViewer = isAdmin
        ? sharesOwnershipDocuments
        : sharesOwnershipDocuments.filter(
            (cert) => !String(cert.name || '').includes('Certificate2024')
          );

      if (hasShowDetails()) {
        console.log(
          '🚀 cclog ~ DocumentsModule ~ ownershipCertsForViewer:',
          ownershipCertsForViewer
        );
      }

      ownershipCertsForViewer.forEach((cert) => {
        list.push({
          ...cert,
          name: t(cert.name),
          description: t(cert.description),
        });
      });
    }

    if (partner && hasCapitalHoldingCertificate && societyId) {
      list.push({
        ...(virtualAutogeneratedDocBase as Document),
        _id: AUTOGENERATED_CERT_DOC_IDS.capitalHolding,
        name: t('CapitalHoldingCertificate'),
        description: '',
        date: now,
        society: societyId,
      });
    }

    if (partner && hasIrpfCertificate && societyId) {
      list.push({
        ...(virtualAutogeneratedDocBase as Document),
        _id: AUTOGENERATED_CERT_DOC_IDS.irpf,
        name: t('IRPFCertificate'),
        description: '',
        date: now,
        society: societyId,
      });
    }

    return list;
  }, [
    hasCapitalHoldingCertificate,
    hasSharesOwnershipCertificate,
    hasIrpfCertificate,
    actualSociety?._id,
    partner,
    sharesOwnershipDocuments,
    isAdmin,
    t,
  ]);

  const renderAutogeneratedDownload = useCallback(
    (document: Document) => {
      if (!partner || !actualSociety) {
        return null;
      }
      if (document._id === AUTOGENERATED_CERT_DOC_IDS.capitalHolding) {
        return (
          <GenerateCertificateTableDownloadButton
            generate={() =>
              generateCapitalHoldingCertificate({
                partner,
                society: actualSociety,
              })
            }
          />
        );
      }
      if (document._id === AUTOGENERATED_CERT_DOC_IDS.irpf) {
        return (
          <GenerateCertificateTableDownloadButton
            generate={() =>
              generateIrpfCertificate({ partner, society: actualSociety })
            }
          />
        );
      }
      return null;
    },
    [actualSociety, partner]
  );

  return (
    <>
      <div className="nk-block-head">
        <div className="nk-block-between g-3 d-flex flex-column flex-md-row">
          <div className="nk-block-head-content">
            <h4 className="nk-block-title">{title}</h4>
            <div className="nk-block-des">
              <p>{subtitle}</p>
            </div>
          </div>

          <div className="nk-block-head-content">
            {isAdmin && (
              <div className="nk-block float-right">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={actions.handleUploadDocument}
                  disabled={isReadOnly}
                >
                  <em className="icon ni ni-upload mr-1" />
                  {t('UpDocument')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {autogeneratedDocuments.length > 0 && (
        <DocumentsTable
          title={t('Certificados autogenerados')}
          documents={autogeneratedDocuments}
          renderDownloadButton={renderAutogeneratedDownload}
        />
      )}

      {documents.length > 0 && (
        <DocumentsTable
          title={t('Otros documentos')}
          documents={documents}
          handleRemoveDocument={actions.handleRemoveDocument}
        />
      )}
    </>
  );
};

export default DocumentsModule;
